import React, { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import {
  LAYOUT_TRANSITION_CLASS,
  SIDEBAR_WIDTH_COLLAPSED_CLASS,
  SIDEBAR_WIDTH_EXPANDED_CLASS,
} from '@/lib/layout/layoutTokens';
import { APP_NAVIGATION_ID, type NavigationShellState } from '@/hooks/useNavigationShellState';

export interface ResponsiveNavigationSidebarProps {
  state: NavigationShellState;
  label: string;
  children: React.ReactNode;
}

/** One width owner and one accessible mobile-drawer contract for all portals. */
export function ResponsiveNavigationSidebar({
  state,
  label,
  children,
}: ResponsiveNavigationSidebarProps) {
  const asideRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!state.isMobile || !state.isMobileNavigationOpen) return;
    const aside = asideRef.current;
    if (!aside) return;

    const focusableSelector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');
    const initialTarget = aside.querySelector<HTMLElement>(focusableSelector);
    window.requestAnimationFrame(() => initialTarget?.focus());

    const trapFocus = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      const focusable = Array.from(aside.querySelectorAll<HTMLElement>(focusableSelector))
        .filter((element) => !element.hasAttribute('hidden'));
      if (focusable.length === 0) {
        event.preventDefault();
        aside.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener('keydown', trapFocus);
    return () => window.removeEventListener('keydown', trapFocus);
  }, [state.isMobile, state.isMobileNavigationOpen]);

  return (
    <>
      {state.isMobile && state.isMobileNavigationOpen && (
        <button
          type="button"
          className="absolute inset-0 z-40 overlay-scrim"
          onClick={() => state.dismissMobileNavigation(true)}
          aria-label="Close navigation"
          tabIndex={-1}
        />
      )}

      {state.isNavigationVisible && (
        <aside
          ref={asideRef}
          id={APP_NAVIGATION_ID}
          className={cn(
            state.isMobile
              ? 'absolute inset-y-0 left-0 z-50'
              : 'relative z-[41] flex-shrink-0',
            state.isSidebarCollapsed
              ? SIDEBAR_WIDTH_COLLAPSED_CLASS
              : SIDEBAR_WIDTH_EXPANDED_CLASS,
            LAYOUT_TRANSITION_CLASS,
            'ax-nav-shell overflow-hidden',
          )}
          aria-label={label}
          tabIndex={-1}
        >
          {children}
        </aside>
      )}
    </>
  );
}

export default ResponsiveNavigationSidebar;
